import React, { useState, useEffect, useCallback } from 'react';

import { parseISO, format } from 'date-fns';
import { Container, Button } from 'react-bootstrap';
import { FaPrint } from 'react-icons/fa';
import { Link } from 'react-router-dom';
import { useToast } from '../../../hooks/toast';
import api from '../../../services/api';

import { ORIGEM_CONTRATO } from '../../../utils/funcoes';

import Loading from '../../Loading';
import getResponseErrors from '../../../utils/getResponseErrors';
import Assinado from './Assinado';

interface IContrato {
  ConCodigo: number;
  ConDataInicio: Date;
  ConDataFim: Date;
  ConDataInicioFormatado: string;
  ConDataFimFormatado: string;
  ConValor: number;
  ConTipo: 1 | 2 | 3 | 4 | 6 | 7 | 10 | 11;
  ConTitulo: string;
  ConAssinado: boolean;
}

export interface IContratoAssinado {
  ConCodigo: number;
  ConAssinado: boolean;
  PlaContratoAssinado: boolean;
}

type ComponentProps = {
  PlaCodigo: number;
  handleAssinouContrato(contratoAssinado: boolean): void;
};

const CartaoSaudeContrato: React.FC<ComponentProps> = ({
  PlaCodigo,
  handleAssinouContrato,
}) => {
  const { addToast } = useToast();
  const [loading, setLoading] = useState(false);

  const [contratos, setContratos] = useState<IContrato[] | undefined>(
    undefined,
  );

  useEffect(() => {
    async function loadDados(): Promise<void> {
      setLoading(true);
      try {
        const response = await api.get<IContrato[]>(
          `planos/contratoscartao/${PlaCodigo}`,
        );

        const contratosFormatados = response.data.map(con => {
          return {
            ...con,
            ConDataInicioFormatado: format(
              parseISO(con.ConDataInicio.toString()),
              'dd/MM/yyyy',
            ),
            ConDataFimFormatado: format(
              parseISO(con.ConDataFim.toString()),
              'dd/MM/yyyy',
            ),
            ConTitulo: ORIGEM_CONTRATO[con.ConTipo],
          };
        });

        setContratos(contratosFormatados);
      } catch (error) {
        const mensagemErro = getResponseErrors(error);
        addToast({
          title: 'Não foi possível buscar os dados',
          type: 'error',
          description: mensagemErro,
        });
      } finally {
        setLoading(false);
      }
    }
    if (PlaCodigo && PlaCodigo > 0) loadDados();
  }, [PlaCodigo, addToast]);

  const handleSetAssinado = useCallback(
    ({ PlaContratoAssinado, ConCodigo, ConAssinado }: IContratoAssinado) => {
      const novoArray = contratos?.map(itens => {
        if (itens.ConCodigo === ConCodigo) return { ...itens, ConAssinado };
        return itens;
      });

      setContratos(novoArray);
      handleAssinouContrato(PlaContratoAssinado);
    },
    [contratos, handleAssinouContrato],
  );

  const handleRefazContrato = useCallback(
    async (conCodigo: number) => {
      setLoading(true);
      try {
        await api.put(`/planos/contrato/${conCodigo}`);
      } catch (error) {
        const mensagemErro = getResponseErrors(error);
        addToast({
          title: 'Não foi possível refazer o contrato',
          type: 'error',
          description: mensagemErro,
        });
      } finally {
        setLoading(false);
      }
    },
    [addToast],
  );

  return (
    <Container style={{ height: '100%', minHeight: '300px' }}>
      {loading && <Loading />}

      {contratos && contratos.length > 0 && (
        <table
          className="table table-hover table-sm table-font-small table-bordered"
          style={{ borderWidth: '0px' }}
        >
          <thead>
            <tr className="bg-light text-center">
              <th style={{ minWidth: '100px' }}>Contrato</th>
              <th style={{ minWidth: '100px' }}>Data Início</th>
              <th style={{ minWidth: '100px' }}>Data Fim</th>
              <th style={{ width: '70px' }}>Visualizar</th>
              <th style={{ width: '70px' }}>Assinado</th>
              <th style={{ width: '70px' }}>-</th>
            </tr>
          </thead>
          <tbody>
            {contratos &&
              contratos.map(rel => (
                <tr
                  key={`des-${rel.ConCodigo}`}
                  style={{
                    fontSize: 'Small',
                    height: '30px',
                  }}
                >
                  <td>{rel.ConTitulo}</td>
                  <td>{rel.ConDataInicioFormatado}</td>
                  <td>{rel.ConDataFimFormatado}</td>
                  <td>
                    <Link
                      style={{ textAlign: 'center' }}
                      to={`/cartaosaudeimpressao/${rel.ConCodigo}/contrato`}
                      target="_blank"
                    >
                      <FaPrint size={26} />
                    </Link>
                  </td>
                  <td>
                    <Assinado
                      ConCodigo={rel.ConCodigo}
                      ConAssinado={rel.ConAssinado}
                      handleAssinado={handleSetAssinado}
                    />
                  </td>
                  <td>
                    {!rel.ConAssinado && (
                      <Button
                        type="button"
                        variant="danger"
                        size="sm"
                        className="float-right ml-2"
                        onClick={() => handleRefazContrato(rel.ConCodigo)}
                        disabled={loading}
                      >
                        Refazer
                      </Button>
                    )}
                  </td>
                </tr>
              ))}
          </tbody>
        </table>
      )}
    </Container>
  );
};

export default CartaoSaudeContrato;
